import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { CreateFileAttachmentInput } from '@releve/shared';
import { supabase } from '@/integrations/supabase/client';
import { authedDelete, authedPost } from '@/lib/api';
import { vaultAttachmentsPath, vaultFilePath } from '@/hooks/useVaultMutations';
import { toast } from '@/hooks/useToast';
import { storageSafeFileName } from '@releve/shared';

/**
 * WRITES GO THROUGH THE BACKEND. `supabase` survives in this file for exactly
 * two things — the storage upload (browser-side by design; see
 * docs/BACKEND_API.md) and the two junction SELECTs. The `files` INSERT and
 * the `transaction_attachments` INSERT it used to do are now ONE atomic
 * `POST /api/v1/files/orgs/:orgId/attachments`, and the `files` DELETE is
 * `DELETE /api/v1/files/orgs/:orgId/files/:fileId?attachment_id=:id`.
 */

export interface ActivityAttachment {
  id: string;
  activity_id: string;
  file_id: string;
  attachment_type: 'activity';
  created_at: string;
  created_by: string;
  file: {
    id: string;
    name: string;
    file_path: string;
    file_size: number;
    content_type: string;
  };
}

/**
 * Hook to fetch attachments for a sustainability activity
 */
export const useActivityAttachments = (activityId?: string) => {
  return useQuery({
    queryKey: ['activity-attachments', activityId],
    queryFn: async () => {
      if (!activityId) return [];

      const { data, error } = await supabase
        .from('transaction_attachments')
        .select(`
          *,
          file:files(id, name, file_path, file_size, content_type)
        `)
        .eq('activity_id', activityId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []) as ActivityAttachment[];
    },
    enabled: !!activityId,
  });
};

/**
 * Hook to upload attachments for a sustainability activity
 */
export const useUploadActivityAttachments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      activityId,
      files,
    }: {
      organizationId: string;
      activityId: string;
      files: File[];
    }) => {
      // No `supabase.auth.getUser()` here: `created_by` is pinned server-side
      // from the verified token, so the browser never supplies an identity.
      const uploadedFiles: ActivityAttachment[] = [];

      for (const file of files) {
        try {
          // 1. Upload file to storage
          // Key-safe object name (Storage rejects e.g. the narrow no-break
          // space in macOS screenshot names); `name` below keeps the original.
          const safeName = storageSafeFileName(file.name);
          const storagePath = `${organizationId}/activities/${activityId}/${safeName}`;
          const { error: uploadError } = await supabase.storage
            .from('files')
            .upload(storagePath, file, {
              cacheControl: '3600',
              upsert: false,
            });

          let finalPath = storagePath;
          let finalFileName = file.name;

          if (uploadError) {
            // If file already exists, try with timestamp
            const timestamp = Date.now();
            const fileExt = file.name.split('.').pop();
            const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
            finalFileName = `${fileNameWithoutExt}-${timestamp}.${fileExt}`;
            const safeDot = safeName.lastIndexOf('.');
            const safeStem = safeDot > 0 ? safeName.slice(0, safeDot) : safeName;
            const safeExt = safeDot > 0 ? safeName.slice(safeDot) : '';
            finalPath = `${organizationId}/activities/${activityId}/${safeStem}-${timestamp}${safeExt}`;

            const { error: retryError } = await supabase.storage
              .from('files')
              .upload(finalPath, file, {
                cacheControl: '3600',
                upsert: false,
              });

            if (retryError) throw retryError;
          }

          // 2. Create the file row AND the junction row in ONE atomic call.
          //    `file_type` is NOT sent — the handler resolves it to 'activity'
          //    for an activity parent (the body schema is `.strict()` and
          //    would 400 on the extra key); `organization_id` is derived from
          //    the parent inside the proc, and `created_by` from the token.
          const { attachment } = await authedPost<{ attachment: ActivityAttachment }>(
            vaultAttachmentsPath(organizationId),
            {
              parent: { kind: 'activity', activity_id: activityId },
              attachment_type: 'activity',
              file: {
                name: finalFileName,
                file_path: finalPath,
                file_size: file.size,
                content_type: file.type,
              },
            } satisfies CreateFileAttachmentInput,
          );

          uploadedFiles.push(attachment);
        } catch (error) {
          console.error(`Failed to upload file ${file.name}:`, error);
          // Continue with other files even if one fails
        }
      }

      return uploadedFiles;
    },
    onSuccess: (_, { organizationId, activityId }) => {
      queryClient.invalidateQueries({ queryKey: ['activity-attachments', activityId] });
      // The rows these uploads create carry organization_id, so they show up in
      // the Vault — which never used to be refreshed after an upload.
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      toast.success('Attachments uploaded successfully');
    },
    onError: (error: Error) => {
      console.error('Error uploading attachments:', error);
      toast.errorTemplates.generic();
    },
  });
};

/**
 * Hook to delete an activity attachment
 */
export const useDeleteActivityAttachment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationId,
      attachmentId,
    }: {
      organizationId: string;
      attachmentId: string;
      /** Not used by the write — it is the invalidation key in onSuccess. */
      activityId: string;
    }) => {
      // 1. Resolve attachment -> file. A READ, so it stays on supabase.
      const { data: attachment, error: readError } = await supabase
        .from('transaction_attachments')
        .select('file_id')
        .eq('id', attachmentId)
        .single();

      if (readError) throw readError;
      // The old code SILENTLY no-oped here (an `if (attachment?.file_id)`
      // guard), so a vanished junction row looked like a successful delete.
      // Now it surfaces through the error toast.
      if (!attachment?.file_id) {
        throw new Error('Attachment not found');
      }

      // 2. ONE call: the backend pre-reads the row, best-effort removes the
      //    storage object and deletes the FILES row. The junction row goes
      //    with it — transaction_attachments.file_id is ON DELETE CASCADE, so
      //    deleting the junction row explicitly is the redundant statement,
      //    never the files delete (dropping THAT would orphan the file row and
      //    leave it sitting in the Vault).
      //
      //    `?attachment_id` says WHICH attachment this delete comes from. The
      //    endpoint counts the file's OTHER references and 409s if any remain:
      //    a file only this activity holds is deleted, one something else also
      //    holds is refused. The Vault's own Delete omits the parameter and is
      //    therefore refused for any attached file at all.
      await authedDelete<unknown>(
        vaultFilePath(organizationId, attachment.file_id, attachmentId),
      );
    },
    onSuccess: (_, { organizationId, activityId }) => {
      queryClient.invalidateQueries({ queryKey: ['activity-attachments', activityId] });
      queryClient.invalidateQueries({ queryKey: ['files', organizationId] });
      toast.success('Attachment deleted');
    },
    onError: (error: Error) => {
      console.error('Error deleting attachment:', error);
      toast.errorTemplates.delete('attachment');
    },
  });
};

/**
 * Hook to get a signed URL for downloading an attachment
 */
export const useGetAttachmentUrl = () => {
  return useMutation({
    mutationFn: async (filePath: string) => {
      const { data, error } = await supabase.storage
        .from('files')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    },
  });
};
